import { useContext, useEffect, useId, useLayoutEffect, useCallback, useMemo } from "react";

import { FloatingPortal } from "@floating-ui/react";
import { isNodeSelection, posToDOMRect } from "@tiptap/react";
import { observer } from "mobx-react-lite";

import {
  suggestionOptionId,
  suggestionSubmenuId,
} from "@/components/suggestions/navigation/SuggestionsListProvider";
import { groupsJson } from "@/components/suggestions/providers/data";
import { isSlashCommandSuggestion } from "@/components/suggestions/providers/data/slashCommandItems";
import { Suggestions } from "@/components/suggestions/Suggestions";
import { SuggestionsContext } from "@/components/suggestions/SuggestionsProvider";
import { handlePickSuggestion } from "@/components/suggestions/useSuggestions";
import { useSuggestionsFloating } from "@/components/suggestions/useSuggestionsFloating";
import { isMathConversionSuggestion } from "@/components/suggestions/utils";
import { AddLinkForm } from "@/components/tiptap/add-link/AddLinkForm";
import { tagAutoMathUndoCursor } from "@/components/tiptap/Collaboration";
import { TextColorPopover } from "@/components/tiptap/color/TextColorPopover";
import { getPopoverRange } from "@/components/tiptap/editor-popover/EditorPopover";
import { INLINE_TEXT_STYLES } from "@/components/tiptap/inline-text-style/inlineTextStyles";
import { insertInlineExpressionAsDisplayMode } from "@/components/tiptap/latex-input/utils";
import { executeSlashCommand } from "@/components/tiptap/slash-menu/executeSlashCommand";
import { insertInlineExpressionAndFocus } from "@/components/tiptap/tiptap-expression/utils";
import { triggerKeyboardInput } from "@/input/handleKeyboardInput";
import { applyGroupSymbols, shouldApplyGroupSymbols } from "@/input/utils/openGroup";
import { DEFAULT_CURSOR_CONTEXT_ID } from "@/models/constants";
import { InlineNodeModel } from "@/models/nodes/InlineNodeModel";
import { isSymbolKey } from "@/utils/input";

import {
  isAddLinkSuggestion,
  isInlineTextStyleSuggestion,
  isLinkToVariableSuggestion,
  isRemoveLinkSuggestion,
  isRemoveVariableLinkSuggestion,
  linkToVariableSymbol,
  isInsertMathSuggestion,
} from "./utils";

import type { ProviderSearchOptions, Suggestion } from "@/components/suggestions/types";
import type { ChatModel } from "@/models/chat/ChatModel";
import type { DocumentModel } from "@/models/DocumentModel";

export type TiptapSuggestionsRenderPlace = "tiptap" | "chat";

type Props = {
  doc: DocumentModel;
  chat?: ChatModel;
};

export const TiptapSuggestions = observer(({ doc, chat }: Props) => {
  const { editor, input, cursorContextId, cursor } = doc;
  const { suggestionsAnchorId, suggestionsRenderPlace } = useContext(SuggestionsContext);

  const open =
    cursorContextId === DEFAULT_CURSOR_CONTEXT_ID &&
    (!editor.editorState.isExpression ||
      (editor.suggestions.pointerAnchor !== null && !cursor.isActive)) &&
    editor.suggestions.available &&
    editor.suggestions.mode !== "idle";

  const menuId = useId();
  const activeOptionId =
    open && editor.suggestions.visible
      ? suggestionOptionId(menuId, editor.suggestions.nestedListId, editor.suggestions.activeIndex)
      : null;
  const { tiptap } = editor;
  const { nestedListId } = editor.suggestions;
  // The submenu is portaled out of the menu, so it is named alongside the menu.
  const controlledIds = [menuId, nestedListId ? suggestionSubmenuId(menuId, nestedListId) : null]
    .filter(Boolean)
    .join(" ");

  // Focus stays in the editor while the menu is open, so the editor plays the combobox: it names
  // the menu and points at the active item, which screen readers then announce as focused.
  useEffect(() => {
    // Reading the view of an editor that has not mounted yet throws.
    if (!tiptap?.isInitialized || !activeOptionId) return;

    const editorDom = tiptap.view.dom;

    editorDom.setAttribute("aria-autocomplete", "list");
    editorDom.setAttribute("aria-controls", controlledIds);
    editorDom.setAttribute("aria-activedescendant", activeOptionId);

    return () => {
      editorDom.removeAttribute("aria-autocomplete");
      editorDom.removeAttribute("aria-controls");
      editorDom.removeAttribute("aria-activedescendant");
    };
  }, [tiptap, controlledIds, activeOptionId]);

  const { refs, context } = useSuggestionsFloating({
    open,
    context: "text",
    place: suggestionsRenderPlace || "tiptap",
    pointerAnchor: editor.suggestions.pointerAnchor,
    onDismiss: () => editor.suggestions.reset(),
  });

  // @see https://github.com/ueberdosis/tiptap/issues/2305#issuecomment-1201373762
  useLayoutEffect(() => {
    refs.setReference({
      getBoundingClientRect() {
        if (!editor.tiptap) return new DOMRect();
        const { view } = editor.tiptap;
        const { selection } = editor.tiptap.state;
        const { ranges } = selection;
        const from = Math.min(...ranges.map((range) => range.$from.pos));
        const to = Math.max(...ranges.map((range) => range.$to.pos));

        if (isNodeSelection(selection)) {
          const node = view.nodeDOM(from) as HTMLElement | null;

          if (node) {
            return node.getBoundingClientRect();
          }
        }

        return posToDOMRect(view, from, to);
      },
    });
  }, [refs, editor.tiptap, editor.tiptap?.state.selection]);

  const handleSuggestionsPointerDown = useCallback(() => {
    editor.suggestions.pointerTouch = true;
  }, [editor.suggestions]);

  // pointerup fires before click, so clearing here lets the selection complete
  // before blur can dismiss the suggestion box on the next interaction
  const handleSuggestionsPointerUp = useCallback(() => {
    editor.suggestions.pointerTouch = false;
  }, [editor.suggestions]);

  const handleSelect = useCallback(
    (suggestion: Suggestion, e?: KeyboardEvent, isSelectedManually = false) => {
      const { tiptap } = editor;
      if (!tiptap) {
        return;
      }

      if (suggestion.type === "color" && suggestion.target === "text") {
        editor.suggestions.reset();
        tiptap.commands.openPopover(() => <TextColorPopover doc={doc} />);
        return;
      }

      if (suggestion.type !== "single" && suggestion.type !== "multi") {
        handlePickSuggestion(doc, suggestion);
        return;
      }

      if (isInsertMathSuggestion(suggestion)) {
        editor.suggestions.reset();
        insertInlineExpressionAndFocus(doc, { keepWhenEmpty: true });
        return;
      }

      if (isAddLinkSuggestion(suggestion)) {
        editor.suggestions.reset();
        tiptap.commands.openPopover((close) => (
          <AddLinkForm
            onSubmit={(href) => {
              const range = getPopoverRange(tiptap.state);
              if (range) {
                tiptap
                  .chain()
                  .setTextSelection(range)
                  .setLink({ href })
                  .setTextSelection(range.to)
                  .focus()
                  .run();
              }
              close();
            }}
            onClose={close}
          />
        ));
        return;
      }

      if (isLinkToVariableSuggestion(suggestion)) {
        tiptap
          .chain()
          .setVariableLink(linkToVariableSymbol(suggestion))
          .setTextSelection(tiptap.state.selection.to)
          .focus()
          .run();
        editor.suggestions.reset();
        return;
      }

      if (isInlineTextStyleSuggestion(suggestion)) {
        if (isRemoveVariableLinkSuggestion(suggestion)) {
          tiptap.chain().focus().unsetVariableLink().run();
        } else if (isRemoveLinkSuggestion(suggestion)) {
          tiptap.chain().focus().unsetLink().run();
        } else {
          INLINE_TEXT_STYLES.find((style) => style.key === suggestion.item.key)?.apply(doc);
        }

        editor.suggestions.reset();
        return;
      }

      const mathRange = doc.selection.range;
      if (doc.selection.isMathOnlySelection && mathRange) {
        doc.cursor.moveTo(mathRange.node, mathRange.head, { resetSelection: false });
        handlePickSuggestion(doc, suggestion, { isSelectedManually });
        return;
      }

      let range = doc.input.inputPosRange;
      const { latexInput } = doc.editor.editorState;
      if (latexInput) {
        range = {
          from: latexInput.start.pos,
          to: latexInput.end
            ? latexInput.end.pos + latexInput.end.type.length
            : latexInput.start.pos + latexInput.start.type.length + latexInput.content.length,
        };
      }

      if (
        !isSelectedManually &&
        suggestion.type === "single" &&
        suggestion.canBeAutocompleted === false
      ) {
        return;
      }

      if (suggestion.type === "single" && isSlashCommandSuggestion(suggestion)) {
        // Run outside doc.transact to match the right-sidebar "Add block" flow.
        executeSlashCommand(doc, range.from, range.to, suggestion.item.kind);
        doc.input.reset();
        return;
      }

      if (e?.code === "Period" || e?.code === "Space") {
        const { nodeBefore } = tiptap.state.selection.$from;
        if (nodeBefore?.text && /^\d+\.$/.test(nodeBefore.text)) {
          if (e.code === "Space") {
            triggerKeyboardInput(doc, "Space", e);
          }
          return;
        }
      }

      const isMathConversion = !latexInput && isMathConversionSuggestion(suggestion);

      // When auto-picked by a printable character, that character isn't in the text
      // yet; commit it as plain text first so a single undo keeps it. A manual pick
      // (Enter/Tab/click) replaces text that's already there.
      const autoConvertChar =
        isMathConversion && !isSelectedManually && e != null && isSymbolKey(e) && !e.altKey
          ? e.key
          : null;
      const autoConvertIsSpace = autoConvertChar === " ";
      // Cursor lands right after the typed text (incl. the trigger char) on undo.
      const undoTextCursor = isMathConversion ? range.to + (autoConvertChar?.length ?? 0) : null;

      if (autoConvertChar) {
        e?.preventDefault();
        e?.stopPropagation();

        doc.transact(() => {
          tiptap.chain().insertContentAt(range.to, autoConvertChar).run();
        }, "TiptapSuggestions:autoConvertTypedText");

        if (!autoConvertIsSpace) {
          // The trigger char belongs inside the math node ("1." → 1., "a/" → a/),
          // so the conversion replaces it together with the typed token. (A space
          // stays as trailing text; useSuggestionsActions skips its own space insert
          // for a math conversion — see isMathConversionSuggestion there.)
          range = { from: range.from, to: range.to + autoConvertChar.length };
        }
      }

      let newInlineNode: InlineNodeModel | undefined;

      doc.transact(
        () => {
          let chain = tiptap.chain();

          if (suggestion.type === "single" && suggestion.item.isSymbol) {
            // Insert symbol as text
            chain = chain.deleteRange(range).insertContent(suggestion.item.symbol).focus();
          } else {
            newInlineNode = doc.create(InlineNodeModel);

            // Check if we're in display mode ($$) from latexInput
            const isDisplayMode = latexInput && latexInput.start.type === "$$";

            if (isDisplayMode) {
              // Use display mode insertion (centered paragraph with smart splitting)
              chain = insertInlineExpressionAsDisplayMode(
                chain,
                newInlineNode,
                range.from,
                range.to,
                doc,
                null,
              );
            } else {
              // Auto-conversion passes an explicit insert position so it doesn't depend
              // on the selection (left at the end of the typed text for the undo cursor).
              chain = chain
                .deleteRange(range)
                .insertInlineExpression(newInlineNode, autoConvertChar ? range.from : undefined);
            }

            cursor.moveTo(newInlineNode, 0);

            // pick suggestion before actually deleting current selection, so that the input is not affected
            handlePickSuggestion(doc, suggestion, { isSelectedManually });
          }

          chain.run();

          if (newInlineNode) {
            doc.cursor.refreshTextCursor();
            // usually browsers can handle it - even though keydown was called outside of math input,
            // it still calls input.onChange and updates the value.
            // however it does not work in tests and might not work in some browsers, so we need to update input value manually.
            if (
              e?.key.length === 1 &&
              e.code !== "Space" &&
              !e.ctrlKey &&
              !e.metaKey &&
              !e.altKey
            ) {
              e.preventDefault();
              e.stopPropagation();

              const { nodeBeforeCursor: beforeNode } = doc.cursor;

              if (
                beforeNode?.isInlineEditable() &&
                beforeNode.validateValue(beforeNode.value + e.key)
              ) {
                beforeNode.value += e.key;
              } else {
                const functionNode = shouldApplyGroupSymbols(doc);

                // usecase: "sin("
                if (e.key === "(" && functionNode) {
                  applyGroupSymbols(
                    functionNode,
                    groupsJson.round.openingSymbol.symbol,
                    groupsJson.round.closingSymbol.symbol,
                  );
                } else {
                  input.queueValueOnMathFocus(e.key);
                }
              }
            }
          }
        },
        "TiptapSuggestions:handleSelect",
        // A math conversion becomes its own undo step so a single undo lands on the
        // typed text, separate from prior typing and the converted node's range.
        { forceNewUndoStack: isMathConversion },
      );

      if (undoTextCursor !== null) {
        // Without this, undo restores the new math node's (now text) range as a
        // selection; the tag tells Collaboration to drop a plain cursor there instead.
        tagAutoMathUndoCursor(tiptap.state, undoTextCursor);
      }

      if (autoConvertIsSpace) {
        if (newInlineNode?.nodes.length === 0) {
          // The merge absorbed the converted node into the preceding expression
          // ("[f]+ " → "[f+]") and moves the cursor inside it in a microtask queued
          // during the transaction above; ours is queued later, so it runs after and
          // parks the cursor back in text. Drop the trailing space so the next typed
          // token stays adjacent and merges too ("f + a" → [f+a]), and place the
          // cursor right after the expression — same as Space inside math.
          queueMicrotask(() => {
            doc.input.reset();
            doc.cursor.reset();
            doc.transact(() => {
              tiptap
                .chain()
                .deleteRange({ from: range.from, to: range.from + 1 })
                .focus(range.from)
                .run();
            }, "TiptapSuggestions:exitMergedConversion");
          });
        } else {
          // Leave the cursor after the trailing space in text mode, not inside the math.
          doc.input.reset();
          doc.cursor.reset();
          tiptap
            .chain()
            .focus(range.from + 2)
            .run();
        }
        return;
      }

      // TODO: input focus forces layout - better do it async (but without breaking following suggestions)
      input.focus();
    },
    [doc, input, cursor, editor],
  );

  const providerSearchOptions: ProviderSearchOptions = useMemo(() => {
    if (!doc.id) return {};
    return {
      mention: {
        scope: chat?.uid ? { chatUid: chat.uid } : { documentUid: doc.id },
      },
    };
  }, [doc.id, chat?.uid]);

  const suggestions = open ? (
    <Suggestions
      ref={refs.setFloating}
      id={menuId}
      renderContext="text"
      floatingContext={context}
      value={input.value}
      doc={doc}
      onSelect={handleSelect}
      onPointerDown={handleSuggestionsPointerDown}
      onPointerUp={handleSuggestionsPointerUp}
      onPointerCancel={handleSuggestionsPointerUp}
      providerSearchOptions={providerSearchOptions}
    />
  ) : null;

  // The chat suggestion box is positioned absolutely relative to its own anchor,
  // and this component already renders inside that anchor — so render it inline.
  // A FloatingPortal resolves its container via getElementById and, on a miss or
  // timing race, creates a node at document.body — which lands OUTSIDE the modal
  // chat Dialog, where Headless UI marks it inert/aria-hidden (rendered but
  // invisible). Inline keeps the box inside the anchor, hence inside the dialog.
  if (suggestionsRenderPlace === "chat") {
    return suggestions;
  }

  return <FloatingPortal id={suggestionsAnchorId}>{suggestions}</FloatingPortal>;
});
